import React from "react";
import { Box, Card, Chip, Stack, Typography } from "@mui/material";

export type SummaryTone = "default" | "success" | "warning" | "danger" | "info";

export interface SummaryCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  delta?: string;
  tone?: SummaryTone;
  icon?: React.ReactNode;
}

const toneMap: Record<SummaryTone, { accent: string; bg: string; chipBg: string; chipColor: string }> = {
  default: { accent: "#163A5F", bg: "#FFFFFF", chipBg: "#EEF2F4", chipColor: "#33495C" },
  success: { accent: "#1E8449", bg: "#F4FBF6", chipBg: "#DFF4E7", chipColor: "#1E8449" },
  warning: { accent: "#D68910", bg: "#FFF9F1", chipBg: "#FBE7BE", chipColor: "#9A6708" },
  danger: { accent: "#C0392B", bg: "#FEF4F3", chipBg: "#F5D0CC", chipColor: "#A93226" },
  info: { accent: "#2471A3", bg: "#F5FAFD", chipBg: "#D7EBF8", chipColor: "#2471A3" },
};

export default function SummaryCard({
  title,
  value,
  subtitle,
  delta,
  tone = "default",
  icon,
}: SummaryCardProps) {
  const t = toneMap[tone];

  return (
    <Card
      sx={{
        position: "relative",
        p: 2,
        minHeight: 148,
        borderRadius: "12px",
        border: "1px solid #E2E8EE",
        boxShadow: "0 10px 25px rgba(16,33,49,0.05)",
        bgcolor: t.bg,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          bgcolor: t.accent,
        }}
      />

      <Stack spacing={1.5} sx={{ height: "100%" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#66788A", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {title}
            </Typography>
          </Box>

          {icon ? (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(0,0,0,0.04)",
                color: t.accent,
              }}
            >
              {icon}
            </Box>
          ) : null}
        </Stack>

        <Typography
          sx={{
            fontSize: { xs: 28, md: 32 },
            fontWeight: 800,
            lineHeight: 1.15,
            color: "#17324D",
            letterSpacing: "-0.03em",
          }}
        >
          {value}
        </Typography>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: "auto" }}>
          <Typography sx={{ fontSize: 13, color: "#66788A", lineHeight: 1.5 }}>
            {subtitle || "—"}
          </Typography>

          {delta ? (
            <Chip
              size="small"
              label={delta}
              sx={{
                borderRadius: "8px",
                fontWeight: 700,
                bgcolor: t.chipBg,
                color: t.chipColor,
              }}
            />
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
}
